import {useCallback, useEffect, useLayoutEffect, useRef, useSyncExternalStore} from 'react'
import type {ComponentPropsWithRef, ComponentPropsWithoutRef, ReactNode, Ref} from 'react'
import {clsx} from 'clsx'
import {flushSync} from 'react-dom'
import styles from './CollapsibleRegion.module.css'

type FindableContentElementProps = Omit<ComponentPropsWithoutRef<'div'>, 'hidden'> & {
  hidden?: 'until-found'
  ref?: Ref<HTMLElement>
}

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'github-ui-disclosure-content': FindableContentElementProps
    }
  }
}

export interface CollapsibleRegionProps extends Omit<ComponentPropsWithRef<'div'>, 'id' | 'children' | 'hidden'> {
  id?: string
  open: boolean
  'aria-labelledby'?: string
  onBrowserReveal?: () => void
  children: ReactNode
}

export const CollapsibleRegion = ({
  id,
  open,
  'aria-labelledby': labelledby,
  onBrowserReveal,
  children,
  className,
  ref,
  ...rest
}: CollapsibleRegionProps) => {
  const isSSR = useIsSSR()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const findableContentRef = useRef<HTMLElement | null>(null)
  const revealedByBrowserRef = useRef(false)
  const preHydrationRevealHandledRef = useRef(false)
  const previousOpenRef = useRef<boolean | null>(null)

  // skip animations if the panel is revealed by the browser for better UX and
  // it avoids WebKit/Safari's timing collision between the CSS transition and when the browser measures the match
  const handleBeforeMatch = useCallback(
    (event: Event) => {
      const panel = panelRef.current

      // `beforematch` bubbles, so revealing a nested region also could reach this listener
      if (!panel || event.target !== panel) return

      panel.removeAttribute('inert')
      revealedByBrowserRef.current = true

      // the browser measures and scrolls to the match as soon as this handler returns, so the panel
      // must be laid out before that, and whether the reveal was accepted must be known here
      if (onBrowserReveal) {
        // the cost is very well bounded to just find-in-page matching interactions
        // eslint-disable-next-line @eslint-react/dom-no-flush-sync
        flushSync(onBrowserReveal)
      }

      if (!previousOpenRef.current) {
        revealedByBrowserRef.current = false
        hidePanel(panel)
      }
    },
    [onBrowserReveal],
  )

  useEffect(() => {
    const panel = panelRef.current
    if (!panel) return
    panel.addEventListener('beforematch', handleBeforeMatch)
    return () => panel.removeEventListener('beforematch', handleBeforeMatch)
  }, [handleBeforeMatch])

  useLayoutEffect(() => {
    const panel = panelRef.current
    const findableContent = findableContentRef.current
    if (!panel || !findableContent) return

    if (isSSR) {
      if (!open && !findableContent.hasAttribute('hidden') && !preHydrationRevealHandledRef.current) {
        preHydrationRevealHandledRef.current = true
        revealedByBrowserRef.current = true
        onBrowserReveal?.()

        queueMicrotask(() => {
          if (panel.getAttribute('aria-hidden') === 'true') {
            hidePanel(panel)
            setPanelHeight(panel, '0px')
          }
        })
      }
      return
    }

    const revealedByBrowser = revealedByBrowserRef.current
    revealedByBrowserRef.current = false

    const skipAnimation =
      previousOpenRef.current === null || revealedByBrowser || typeof panel.getAnimations !== 'function'

    if (skipAnimation) {
      if (open) {
        panel.removeAttribute('inert')
        panel.removeAttribute('hidden')
        setPanelHeight(panel, 'auto')
      } else {
        hidePanel(panel)
        setPanelHeight(panel, '0px')
      }
    } else if (open !== previousOpenRef.current) {
      if (open) {
        panel.removeAttribute('inert')
        panel.removeAttribute('hidden')
        setPanelHeight(panel, `${panel.scrollHeight}px`)
        void afterAnimations(panel, () => setPanelHeight(panel, 'auto'))
      } else {
        panel.setAttribute('inert', '')
        setPanelHeight(panel, `${panel.scrollHeight}px`)
        panel.getBoundingClientRect()
        setPanelHeight(panel, '0px')
        void afterAnimations(panel, () => hidePanel(panel))
      }
    }

    previousOpenRef.current = open
  }, [open, isSSR, onBrowserReveal])

  return (
    <div
      {...rest}
      ref={node => {
        panelRef.current = node
        if (typeof ref === 'function') return ref(node)
        if (ref) ref.current = node
      }}
      id={id}
      role={labelledby ? 'region' : undefined}
      aria-labelledby={labelledby}
      aria-hidden={!open || undefined}
      className={clsx(styles.Panel, className)}
    >
      <FindableContent ref={findableContentRef} hidden={isSSR && !open ? 'until-found' : undefined}>
        {children}
      </FindableContent>
    </div>
  )
}

const FindableContent = ({
  children,
  hidden,
  ref,
}: {
  children: ReactNode
  hidden?: 'until-found'
  ref: Ref<HTMLElement>
}) => (
  <github-ui-disclosure-content className={styles.FindableContent} hidden={hidden} ref={ref} suppressHydrationWarning>
    {children}
  </github-ui-disclosure-content>
)

const subscribeToNothing = () => () => {}

const useIsSSR = () =>
  useSyncExternalStore(
    subscribeToNothing,
    () => false,
    () => true,
  )

const setPanelHeight = (panel: HTMLElement, value: string) => {
  panel.style.setProperty('--disclosure-panel-height', value)
}

const hidePanel = (panel: HTMLElement) => {
  const untilFoundSupported = 'onbeforematch' in panel.ownerDocument.body
  panel.setAttribute('hidden', untilFoundSupported ? 'until-found' : '')
  panel.removeAttribute('inert')
}

const afterAnimations = async (panel: HTMLElement, apply: () => void) => {
  try {
    await Promise.all(panel.getAnimations().map(animation => animation.finished))
  } catch {
    return
  }
  apply()
}
